/**
 * 문제 목록 응답에 섞여 있는 임시 저장 답안(suspendedAnswer)을 화면이 쓰는 답안 목록 형태로 바꾼다.
 *
 * `StudentHome.tsx`, `pages/problem/index.tsx`가 이전 풀이 중 저장된 답안을 이어서
 * 보여줄 때 호출한다. 객관식(1~5) 문제는 답 번호로 `questionViewList`에서 채점 정보를
 * 찾고, O/X 문제는 인덱스 0(O)/1(X) 고정 위치에서 채점 정보를 찾는다.
 */
import { TempAnswerProps } from "@/types/tempAnswerProps";
import { AnswerProps } from "@/types/answerProps";

/**
 * @param problemData 문제 목록 응답 원본(각 항목에 `suspendedAnswer`, `questionId`, `questionViewList` 포함)
 * @returns 임시 저장 답안이 하나도 없으면 빈 배열. 각 임시 답안 중 answer가 "1"~"5" 또는
 *   "O"/"X"가 아니거나, questionId가 일치하는 문제를 못 찾거나, 해당 인덱스의
 *   questionViewList 항목이 없으면 그 답안은 결과에서 조용히 빠진다(에러를 던지지 않음).
 */
export const processSuspendedAnswers = (problemData: any[]) => {
  // 1. suspendedAnswer만 뽑아서 배열로 만듦
  const suspendedAnswers: TempAnswerProps[] = problemData
    .map((problem) => problem.suspendedAnswer)
    .filter((answer) => answer !== null); // null인 값 제외

  if (suspendedAnswers.length === 0) {
    // suspendedAnswer가 없는 경우
    return []; // 또는 적절한 처리 (e.g. null이나 메시지 반환)
  }

  // 2. answerList에 결과를 저장
  const answerList: AnswerProps[] = [];

  // 3. suspendedAnswer 순회하면서 처리
  suspendedAnswers.forEach((suspendedAnswer) => {
    // 해당 questionId와 일치하는 problemData의 아이템 찾기
    const problem = problemData.find(
      (item) => item.questionId === suspendedAnswer.questionId
    );

    // !! answer가 ""인 경우는 어떻게 되는거지?
    if (problem) {
      // 4. answer가 1, 2, 3, 4, 5 중 하나인 경우
      if (["1", "2", "3", "4", "5"].includes(suspendedAnswer.answer)) {
        // answer를 number형식으로 변환하고 -1를 뺀 것을 인덱스로 설정
        const answerIndex = parseInt(suspendedAnswer.answer, 10) - 1;

        // questionViewList에서 해당 인덱스의 아이템을 찾기
        const answerItem = problem.questionViewList[answerIndex];

        if (answerItem) {
          // 필요한 속성값들 뽑아서 객체로 만듦
          const answerData: AnswerProps = {
            questionId: suspendedAnswer.questionId,
            answer: suspendedAnswer.answer,
            answerSecond: suspendedAnswer.answerSecond,
            correctType: answerItem.correctType,
            literacyScore: answerItem.literacyScore,
            communicationScore: answerItem.communicationScore,
            logicScore: answerItem.logicScore,
            creativityScore: answerItem.creativityScore,
            applicationScore: answerItem.applicationScore,
            understandingScore: answerItem.understandingScore,
          };

          // answerList 배열에 추가
          answerList.push(answerData);
        }
      }
      // 5. answer가 O/X인 경우
      else if (
        suspendedAnswer.answer === "O" ||
        suspendedAnswer.answer === "X"
      ) {
        // questionViewList에서 해당 answer값을 가진 아이템 찾기
        // const answerItem = problem.questionViewList.find(
        //   (item: QuestionView) => item.content === suspendedAnswer.answer
        // );
        let answerItem;
        if (suspendedAnswer.answer === "O")
          answerItem = problem.questionViewList[0];
        else if (suspendedAnswer.answer === "X")
          answerItem = problem.questionViewList[1];

        if (answerItem) {
          // 필요한 속성값들 뽑아서 객체로 만듦
          const answerData = {
            questionId: suspendedAnswer.questionId,
            answer: suspendedAnswer.answer,
            answerSecond: suspendedAnswer.answerSecond,
            correctType: answerItem.correctType,
            literacyScore: answerItem.literacyScore,
            communicationScore: answerItem.communicationScore,
            logicScore: answerItem.logicScore,
            creativityScore: answerItem.creativityScore,
            applicationScore: answerItem.applicationScore,
            understandingScore: answerItem.understandingScore,
          };

          // answerList 배열에 추가
          answerList.push(answerData);
        }
      }
    }
  });

  return answerList;
};
